/**
 * PATH: packages/email-hub/src/feature/components/RequestRuleDialog.tsx
 * FILE_ID: EMAILHUB-COMPONENT-REQUEST-RULE-DIALOG-001
 * PURPOSE: "Request a rule" — the operator types ONE plain-English filing
 *          instruction ("CommBank bank reminders go to a Records board for
 *          compliance"); the AI proposes a bounded board/sub-board/lane/rule
 *          spec; the operator previews and applies it in one click.
 * WHY: Operator 2026-07-06: "I should be able to instead of spamming the
 *      fucking board selections… request a rule AI… and it proposes the
 *      board / sub board / rule / categorisation and creates it."
 *      First principle (canvas 2026-07-06): the AI proposes, rules enforce —
 *      the LLM runs ONCE here, behind a human-approved preview; enforcement
 *      is the deterministic ClassificationRule engine.
 * FLOW:
 *   ┌──────────┐   ┌─────────────────┐   ┌───────────┐   ┌────────────┐
 *   │ sentence │ → │ proposeRule…    │ → │ preview   │ → │ applyRule… │
 *   │ + email  │   │ (dry-run, 0 wr) │   │ (approve) │   │ (atomic)   │
 *   └──────────┘   └─────────────────┘   └───────────┘   └────────────┘
 * DEPENDENCIES:
 *   - @quotech/kanban-host getSamKanbanApi: proposeRuleRequest /
 *     applyRuleRequest (optional contract methods — callers feature-detect).
 * RELATED:
 *   - EmailPreviewPane.tsx: mounts the chip + dialog in the AI co-pilot strip.
 *   - backend email_kanban/views/rule_requests.py: the two endpoints.
 */
import { useMemo, useState } from 'react'
import { Loader2, Sparkles, Wand2, X } from 'lucide-react'
import type {
  RuleRequestProposal,
  RuleRequestProposeResult,
} from '@quotech/ui/kanban'
import { useToast } from '../../_internal/components/Toast'
import { cn } from '../../_internal/lib/utils'

/** The narrow api slice the dialog needs (feature-detected by the host). */
export interface RuleRequestApiSlice {
  proposeRuleRequest?: (input: {
    organisationId: number
    instruction: string
    emailId?: string
  }) => Promise<RuleRequestProposeResult>
  /** C-01 SSE propose — preferred by AIEmailCopilot when present. */
  proposeRuleRequestStream?: (input: {
    organisationId: number
    instruction: string
    emailId?: string
  }) => Promise<RuleRequestProposeResult>
  applyRuleRequest?: (input: {
    organisationId: number
    proposal: RuleRequestProposal
  }) => Promise<{ rule_id: string }>
}

export interface RequestRuleDialogProps {
  api: RuleRequestApiSlice
  orgId: number
  /** Context email — sender/subject/category prefill the proposal server-side. */
  emailId: string
  senderAddress?: string | null
  category?: string | null
  onClose: () => void
  /** Fires after a successful apply so the host can refetch. */
  onApplied?: () => void
}

/** Pure I→O: trigger enum → operator-readable condition ("sender domain is
 *  edm.cba.com.au"). Falls back to `<trigger> matches <value>` for future
 *  trigger types so the preview never renders blank. */
export function describeRuleTrigger(trigger: string, value: string): string {
  const map: Record<string, string> = {
    SENDER_DOMAIN: 'the sender domain is',
    SENDER_EXACT: 'the sender is exactly',
    SUBJECT_REGEX: 'the subject matches',
    BODY_REGEX: 'the body matches',
    ATTACHMENT_NAME_REGEX: 'an attachment name matches',
    HAS_ATTACHMENT_TYPE: 'it has an attachment of type',
  }
  const lead = map[trigger] ?? `${trigger.toLowerCase().replace(/_/g, ' ')} matches`
  return `${lead} “${value}”`
}

/** Pure I→O: proposal → the full one-sentence preview the operator approves. */
export function describeProposal(proposal: RuleRequestProposal): string {
  const cond = describeRuleTrigger(proposal.rule.trigger, proposal.rule.trigger_value)
  if (proposal.rule.action === 'BLOCK' || !proposal.structure) {
    return `IF ${cond} THEN block it from the boards`
  }
  const path = [
    proposal.structure.board.name,
    proposal.structure.sub_board?.name,
    proposal.structure.lane.name,
  ].filter(Boolean).join(' › ')
  const base = `IF ${cond} THEN file it to ${path}`
  // P33 — when dual-board routing is on, finance/job mirrors are automatic;
  // surface that so the operator knows approve creates the rule + mirror path.
  const boardName = (proposal.structure.board.name || '').toLowerCase()
  if (
    boardName.includes('finance') ||
    boardName.includes('job') ||
    boardName.includes('accounts')
  ) {
    return `${base} (finance/job boards may also receive a mirror when dual-board routing is on)`
  }
  return base
}

function StructureBox({ name, kind, create }: { name: string; kind: string; create: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex flex-col rounded-md border px-2 py-1 min-w-0',
        create ? 'border-primary/50 bg-primary/5' : 'border-border bg-muted/40',
      )}
    >
      <span className="text-xs font-medium truncate">{name}</span>
      <span className="text-[10px] text-muted-foreground">
        {create ? `new ${kind}` : kind}
      </span>
    </span>
  )
}

type Phase = 'input' | 'proposing' | 'preview' | 'applying'

export function RequestRuleDialog({
  api,
  orgId,
  emailId,
  senderAddress,
  category,
  onClose,
  onApplied,
}: RequestRuleDialogProps) {
  const { toast } = useToast()
  const [instruction, setInstruction] = useState('')
  const [phase, setPhase] = useState<Phase>('input')
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<RuleRequestProposeResult | null>(null)

  const senderDomain = useMemo(() => {
    const addr = (senderAddress || '').toLowerCase()
    return addr.includes('@') ? addr.split('@').pop() : null
  }, [senderAddress])

  const propose = () => {
    // Prefer JSON propose — live BE registers /propose/ only. Stream is
    // optional Phase-C; using it first 404'd Create-rule for operators.
    const proposeFn = api.proposeRuleRequest ?? api.proposeRuleRequestStream
    if (!proposeFn || !instruction.trim()) return
    setPhase('proposing')
    setError(null)
    proposeFn({ organisationId: orgId, instruction, emailId })
      .then((r) => {
        setResult(r)
        setPhase('preview')
      })
      .catch((e: Error) => {
        // 422 = rephrase, 502 = AI down — the server message is operator-facing.
        setError(e.message || 'The AI could not propose a rule — try rephrasing.')
        setPhase('input')
      })
  }

  const apply = () => {
    if (!api.applyRuleRequest || !result?.proposal) return
    setPhase('applying')
    setError(null)
    api
      .applyRuleRequest({
        organisationId: orgId,
        proposal: result.proposal,
      })
      .then(() => {
        toast('Rule created — matching emails will file themselves from now on', 'success')
        onApplied?.()
        onClose()
      })
      .catch((e: Error) => {
        setError(e.message || 'Could not create the rule.')
        setPhase('preview')
      })
  }

  const proposal = result?.proposal ?? null
  const matched = result?.matched_existing

  return (
    <div
      data-testid="request-rule-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Request a rule"
        className="w-full max-w-lg rounded-lg border border-border bg-background shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <Wand2 className="h-4 w-4 text-primary" aria-hidden />
          <span className="text-sm font-medium">Request a rule</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {(phase === 'input' || phase === 'proposing') && (
            <>
              <p className="text-xs text-muted-foreground">
                Describe the outcome you want — filing, a subscription board,
                blocking noise. The AI proposes the board, lane and rule;
                nothing is created until you approve it.
              </p>
              <textarea
                autoFocus
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    propose()
                  }
                }}
                rows={3}
                maxLength={1000}
                placeholder='e.g. "SaaS renewals from this sender go to a Subscriptions board › Renewals lane"'
                data-testid="request-rule-instruction"
                className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                <span>Context sent with your sentence:</span>
                {senderDomain && (
                  <span className="rounded-full border border-border px-2 py-0.5">
                    sender: {senderDomain}
                  </span>
                )}
                {category && category !== 'uncategorized' && (
                  <span className="rounded-full border border-border px-2 py-0.5">
                    category: {category}
                  </span>
                )}
              </div>
              {error && (
                <p data-testid="request-rule-error" className="text-xs text-destructive">
                  {error}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={propose}
                  disabled={
                    phase === 'proposing' ||
                    !instruction.trim() ||
                    !(api.proposeRuleRequest || api.proposeRuleRequestStream)
                  }
                  data-testid="request-rule-propose"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {phase === 'proposing' ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  ) : (
                    <Sparkles className="h-3 w-3" aria-hidden />
                  )}
                  {phase === 'proposing' ? 'Asking the AI…' : 'Propose rule'}
                </button>
              </div>
            </>
          )}

          {(phase === 'preview' || phase === 'applying') && proposal && (
            <>
              <p className="text-xs text-muted-foreground">
                Nothing exists yet — approve to create it. Highlighted pieces
                are new; the rest is reused.
              </p>
              {proposal.structure && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="request-rule-structure">
                  <StructureBox
                    name={proposal.structure.board.name}
                    kind="board"
                    create={proposal.structure.board.create}
                  />
                  {proposal.structure.sub_board && (
                    <>
                      <span className="text-muted-foreground text-xs" aria-hidden>›</span>
                      <StructureBox
                        name={proposal.structure.sub_board.name}
                        kind="sub-board"
                        create={proposal.structure.sub_board.create}
                      />
                    </>
                  )}
                  <span className="text-muted-foreground text-xs" aria-hidden>›</span>
                  <StructureBox
                    name={proposal.structure.lane.name}
                    kind="lane"
                    create={proposal.structure.lane.create}
                  />
                </div>
              )}
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs" data-testid="request-rule-sentence">
                {describeProposal(proposal)}
              </p>
              {proposal.explain && (
                <p className="text-[11px] text-muted-foreground italic">{proposal.explain}</p>
              )}
              {matched && matched.count > 0 && (
                <p className="text-[11px] text-muted-foreground" data-testid="request-rule-matched">
                  Would have matched {matched.count} of your last {matched.scanned} emails
                  {matched.sample[0] ? ` (e.g. “${matched.sample[0].subject}”)` : ''}.
                </p>
              )}
              {error && (
                <p data-testid="request-rule-error" className="text-xs text-destructive">
                  {error}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPhase('input')
                    setResult(null)
                    setError(null)
                  }}
                  className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-accent"
                >
                  Rephrase
                </button>
                <button
                  type="button"
                  onClick={apply}
                  disabled={phase === 'applying'}
                  data-testid="request-rule-apply"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {phase === 'applying' && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
                  {phase === 'applying' ? 'Creating…' : 'Create rule'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default RequestRuleDialog
